import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, CheckCircle2, Clock, Utensils, Brain, Microscope, Leaf } from 'lucide-react';
import { Button } from '../ui/Button';
const pressLogos = [{
  name: 'Vogue',
  url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6c/Vogue_logo.svg/2560px-Vogue_logo.svg.png'
}, {
  name: 'Forbes',
  url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0c/Forbes_logo.svg/2560px-Forbes_logo.svg.png'
}, {
  name: 'Elle',
  url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e6/Elle_logo.svg/2560px-Elle_logo.svg.png'
}, {
  name: 'National Geographic',
  url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/67/National_Geographic_Logo.svg/2560px-National_Geographic_Logo.svg.png'
}];
export function HeroSection() {
  const {
    scrollY
  } = useScroll();
  // Refined Parallax Effects - Subtle and purposeful
  const yBackground = useTransform(scrollY, [0, 1000], [0, 200]);
  const rotateCircle = useTransform(scrollY, [0, 1000], [0, 30]);
  const heroImageY = useTransform(scrollY, [0, 1000], [0, 80]);
  const textY = useTransform(scrollY, [0, 500], [0, 50]);
  // Staggered animation variants for text
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1] // Custom ease for "premium" feel
      }
    }
  };
  return <section className="relative bg-[#FDFCF8] pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden min-h-[95vh] flex items-center" data-id="element-2039">
      {/*
            DISTINCTIVE BACKGROUND:
            Replacing generic blobs with a "Technical/Organic" aesthetic.
            1. Dot grid for precision/science.
            2. Large thin circles for holistic/organic feel.
          */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-2040">
        {/* Subtle Dot Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.4]" style={{
        backgroundImage: 'radial-gradient(#A8A29E 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }} data-id="element-2041" />

        {/* Organic/Scientific Circles - Rotating slowly */}
        <motion.div style={{
        y: yBackground,
        rotate: rotateCircle
      }} className="absolute -top-[20%] -right-[10%] w-[80vw] h-[80vw] border-[1px] border-primary-900/5 rounded-full" data-id="element-2042" />
        <motion.div style={{
        y: yBackground,
        rotate: rotateCircle
      }} className="absolute -top-[15%] -right-[5%] w-[60vw] h-[60vw] border-[1px] border-primary-900/5 rounded-full border-dashed" data-id="element-2043" />
      </div>

      <div className="container-custom relative z-10" data-id="element-2044">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center" data-id="element-2045">
          {/* Left Column: Content (7 cols) */}
          <motion.div variants={containerVariants} initial="hidden" animate="visible" style={{
          y: textY
        }} className="lg:col-span-7 flex flex-col items-center lg:items-start text-center lg:text-left space-y-10" data-id="element-2046">
            {/* Scientific/Premium Badge */}
            <motion.div variants={itemVariants} data-id="element-2047">
              <div className="inline-flex items-center gap-3 bg-white px-5 py-2.5 rounded-full border border-stone-200 shadow-sm hover:border-primary-200 transition-colors duration-300 cursor-default group" data-id="element-2048">
                <div className="flex -space-x-3 shrink-0" data-id="element-2049">
                  {[1, 2, 3].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-stone-100 overflow-hidden relative" data-id="element-2050">
                      <img src={`https://i.pravatar.cc/100?img=${i + 30}`} alt="Expert Vet" className="w-full h-full object-cover grayscale opacity-80 group-hover:grayscale-0 transition-all duration-500" data-id="element-2051" />
                    </div>)}
                  <div className="w-8 h-8 rounded-full border-2 border-white bg-primary-50 flex items-center justify-center text-primary-700 text-[10px] font-bold" data-id="element-2052">
                    +50
                  </div>
                </div>
                <div className="h-4 w-px bg-stone-300 mx-1" data-id="element-2053" />
                <span className="text-xs font-medium text-stone-600 tracking-wide uppercase group-hover:text-primary-800 transition-colors" data-id="element-2054">
                  Avalado por veterinarios
                </span>
              </div>
            </motion.div>

            {/* Editorial Headline */}
            <motion.div variants={itemVariants} className="space-y-6 max-w-3xl relative" data-id="element-2055">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-serif font-medium text-charcoal-900 leading-[1.05] tracking-tight" data-id="element-2056">
                Nutrición real, <br data-id="element-2057" />
                <span className="relative inline-block text-primary-900" data-id="element-2058">
                  <i data-id="element-2059">ciencia</i> viva.
                  {/* Distinctive Underline - Not a generic swoosh */}
                  <motion.div initial={{
                  width: 0
                }} animate={{
                  width: '100%'
                }} transition={{
                  delay: 0.8,
                  duration: 1.2,
                  ease: [0.22, 1, 0.36, 1]
                }} className="absolute -bottom-2 left-0 h-1 bg-primary-300/50" data-id="element-2060" />
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-stone-600 font-light leading-relaxed max-w-xl mx-auto lg:mx-0 text-pretty" data-id="element-2061">
                Revolucionamos el plato de tu mascota fusionando{' '}
                <span className="font-medium text-charcoal-900 border-b border-primary-200 hover:bg-primary-50 transition-colors cursor-default" data-id="element-2062">
                  ingredientes frescos
                </span>{' '}
                con{' '}
                <span className="font-medium text-charcoal-900 border-b border-purple-200 hover:bg-purple-50 transition-colors cursor-default" data-id="element-2063">
                  estimulación cognitiva
                </span>
                . El primer sistema de bienestar integral diseñado por expertos.
              </p>
            </motion.div>

            {/* Action Area */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-5 w-full sm:w-auto pt-4" data-id="element-2064">
              <Link to="/productos" className="w-full sm:w-auto group" data-id="element-2065">
                <Button size="xl" variant="primary" withRipple={true} pulse={true} className="w-full sm:w-auto rounded-full font-bold px-10 py-5 text-base shadow-xl shadow-primary-900/5 hover:shadow-primary-900/20 transition-all duration-300 hover:-translate-y-1" rightIcon={<ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" data-id="element-2067" />} data-id="element-2066">
                  Crear Plan Personalizado
                </Button>
              </Link>
              <Link to="/nosotros" className="w-full sm:w-auto group" data-id="element-2068">
                <Button variant="outline" size="xl" withRipple={true} className="w-full sm:w-auto rounded-full border-2 border-stone-200 text-stone-600 font-bold px-10 py-5 text-base hover:bg-stone-50 hover:border-stone-300 transition-all duration-300" leftIcon={<Microscope className="w-5 h-5 text-stone-400 group-hover:text-primary-600 transition-colors" data-id="element-2070" />} data-id="element-2069">
                  La Ciencia Detrás
                </Button>
              </Link>
            </motion.div>

            {/* Value Props - Clean & Minimal */}
            <motion.div variants={itemVariants} className="flex flex-wrap justify-center lg:justify-start gap-x-8 gap-y-4 pt-6 text-sm font-medium text-stone-500" data-id="element-2071">
              {[{
              icon: Leaf,
              text: '100% Natural'
            }, {
              icon: Clock,
              text: 'Cocción Lenta'
            }, {
              icon: CheckCircle2,
              text: 'Grado Humano'
            }].map((item, i) => <div key={i} className="flex items-center gap-2 group cursor-default" data-id="element-2072">
                  <item.icon className="w-4 h-4 text-primary-600 group-hover:scale-110 transition-transform duration-300" data-id="element-2073" />
                  <span className="group-hover:text-primary-800 transition-colors" data-id="element-2074">
                    {item.text}
                  </span>
                </div>)}
            </motion.div>

            {/* Press Logos - Monochrome & Elegant */}
            <motion.div variants={itemVariants} className="pt-12 w-full max-w-2xl border-t border-stone-200 mt-8" data-id="element-2075">
              <div className="flex justify-between items-center gap-8 opacity-40 grayscale hover:grayscale-0 transition-all duration-700" data-id="element-2076">
                {pressLogos.map((logo, i) => <img key={i} src={logo.url} alt={logo.name} className="h-4 sm:h-5 object-contain hover:scale-105 transition-transform duration-300" data-id="element-2077" />)}
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column: Visuals (5 cols) */}
          <motion.div initial={{
          opacity: 0,
          x: 50
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 1,
          delay: 0.4,
          ease: [0.22, 1, 0.36, 1]
        }} style={{
          y: heroImageY
        }} className="lg:col-span-5 relative h-[600px] lg:h-[750px] w-full flex items-center justify-center lg:justify-end" data-id="element-2078">
            {/*
             Distinctive Image Container:
             Using a "Double Exposure" effect or a clean layered card look instead of a basic rounded rect.
             */}
            <div className="relative w-[90%] lg:w-full max-w-md aspect-[3/4]" data-id="element-2079">
              {/* Decorative Frame - "Blueprint" style */}
              <div className="absolute -inset-4 border border-stone-300 rounded-[3rem] z-0 opacity-60" data-id="element-2080" />
              <div className="absolute -inset-2 border border-stone-200 rounded-[2.5rem] z-0 opacity-40" data-id="element-2081" />

              {/* Main Image Card */}
              <div className="relative h-full w-full rounded-[2rem] overflow-hidden shadow-2xl shadow-stone-900/10 z-10 bg-white group" data-id="element-2082">
                <img src="https://images.unsplash.com/photo-1535930749574-1399327ce78f?auto=format&fit=crop&q=80&w=1200" alt="Golden Retriever sano" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" data-id="element-2083" />

                {/* Subtle Gradient Overlay for text readability if needed */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" data-id="element-2084" />
              </div>

              {/* Floating Data Card 1: Nutritional Breakdown */}
              <motion.div initial={{
              opacity: 0,
              x: 40,
              y: 20
            }} animate={{
              opacity: 1,
              x: 0,
              y: 0
            }} transition={{
              delay: 0.8,
              duration: 0.8,
              type: 'spring',
              stiffness: 80,
              damping: 20
            }} whileHover={{
              y: -5,
              scale: 1.02,
              transition: {
                duration: 0.3
              }
            }} className="absolute top-12 -right-6 lg:-right-12 z-20 w-64" data-id="element-2085">
                <div className="bg-white/95 backdrop-blur-md p-4 rounded-xl shadow-lg border border-stone-100 ring-1 ring-black/5" data-id="element-2086">
                  <div className="flex items-center gap-3 mb-3 border-b border-stone-100 pb-2" data-id="element-2087">
                    <div className="p-2 bg-primary-50 rounded-lg text-primary-700" data-id="element-2088">
                      <Utensils className="w-4 h-4" data-id="element-2089" />
                    </div>
                    <div data-id="element-2090">
                      <p className="text-[10px] font-bold text-stone-400 uppercase tracking-wider" data-id="element-2091">
                        Análisis
                      </p>
                      <p className="text-sm font-bold text-charcoal-900" data-id="element-2092">
                        Perfil Nutricional
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3" data-id="element-2093">
                    {/* Custom Data Viz Bars */}
                    {[{
                    label: 'Proteína Real',
                    value: '92%',
                    color: 'bg-primary-500'
                  }, {
                    label: 'Digestibilidad',
                    value: '98%',
                    color: 'bg-amber-500'
                  }].map((stat, i) => <div key={i} data-id="element-2094">
                        <div className="flex justify-between text-[10px] font-medium text-stone-600 mb-1" data-id="element-2095">
                          <span data-id="element-2096">{stat.label}</span>
                          <span className="font-bold" data-id="element-2097">{stat.value}</span>
                        </div>
                        <div className="h-1.5 w-full bg-stone-100 rounded-full overflow-hidden" data-id="element-2098">
                          <motion.div initial={{
                        width: 0
                      }} animate={{
                        width: stat.value
                      }} transition={{
                        duration: 1.5,
                        delay: 1.2 + i * 0.2,
                        ease: 'easeOut'
                      }} className={`h-full rounded-full ${stat.color}`} data-id="element-2099" />
                        </div>
                      </div>)}
                  </div>
                </div>
              </motion.div>

              {/* Floating Data Card 2: Cognitive Health */}
              <motion.div initial={{
              opacity: 0,
              x: -40,
              y: 40
            }} animate={{
              opacity: 1,
              x: 0,
              y: 0
            }} transition={{
              delay: 1,
              duration: 0.8,
              type: 'spring',
              stiffness: 80,
              damping: 20
            }} whileHover={{
              y: -5,
              scale: 1.02,
              transition: {
                duration: 0.3
              }
            }} className="absolute bottom-20 -left-6 lg:-left-12 z-20 w-56" data-id="element-2100">
                <div className="bg-white/95 backdrop-blur-md p-4 rounded-xl shadow-lg border border-stone-100 ring-1 ring-black/5" data-id="element-2101">
                  <div className="flex items-center gap-3 mb-2" data-id="element-2102">
                    <div className="p-2 bg-purple-50 rounded-lg text-purple-700" data-id="element-2103">
                      <Brain className="w-4 h-4" data-id="element-2104" />
                    </div>
                    <div data-id="element-2105">
                      <p className="text-[10px] font-bold text-stone-400 uppercase tracking-wider" data-id="element-2106">
                        Cognitivo
                      </p>
                      <p className="text-sm font-bold text-charcoal-900" data-id="element-2107">
                        Enfoque Mental
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2" data-id="element-2108">
                    <span className="text-2xl font-bold text-purple-900" data-id="element-2109">
                      +45%
                    </span>
                    <span className="text-[10px] leading-tight text-stone-500" data-id="element-2110">
                      Mejora en
                      <br data-id="element-2111" />
                      atención
                    </span>
                  </div>
                </div>
              </motion.div>

              {/* Decorative "Scientific" Elements */}
              <div className="absolute -bottom-10 -right-10 w-24 h-24 bg-pattern-dots opacity-20 z-0" data-id="element-2112" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}